<script setup lang="ts">
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import InputNumber from 'primevue/inputnumber';
import { Button } from '@gpx-spa/ui';
import HeadingSection from '@/components/_common/Heading/HeadingSection.vue';
import SettingsSettingRow from '@/components/settings/SettingsSettingRow.vue';
import {
  ACCOUNT_DEFAULT_CODE,
  useAccountDefaults,
} from '@/composables/useAccountDefaults';
import { COMMON_UPDATE_SUCCESS } from '@/config/constants';
import { setTitle } from '@/utils/title';

/** The geofence form applies the same bound to each allowable parameter. */
const PARAMETER_MAX = 500;

const toast = useToast();
const { readNumber, readBoolean, save, isSaving } = useAccountDefaults();

const alertOnEnterExit = ref(
  readBoolean(ACCOUNT_DEFAULT_CODE.geofenceAlertOnEnterExit) ?? false,
);
const maxDwellDays = ref(readNumber(ACCOUNT_DEFAULT_CODE.geofenceMaxDwellDays));
const maxAssets = ref(readNumber(ACCOUNT_DEFAULT_CODE.geofenceMaxAssets));
const minAssets = ref(readNumber(ACCOUNT_DEFAULT_CODE.geofenceMinAssets));

// An empty allowable parameter is a real state — "no limit" — so it clears the row
// rather than storing a zero, which the geofence form would read as a limit of none
// allowed.
const onSave = () =>
  save({
    [ACCOUNT_DEFAULT_CODE.geofenceAlertOnEnterExit]: alertOnEnterExit.value,
    [ACCOUNT_DEFAULT_CODE.geofenceMaxDwellDays]: maxDwellDays.value ?? null,
    [ACCOUNT_DEFAULT_CODE.geofenceMaxAssets]: maxAssets.value ?? null,
    [ACCOUNT_DEFAULT_CODE.geofenceMinAssets]: minAssets.value ?? null,
  })
    .then(() => toast.success(COMMON_UPDATE_SUCCESS))
    .catch(() => toast.error('Could not update geofence defaults'));

const onReset = () =>
  save({
    [ACCOUNT_DEFAULT_CODE.geofenceAlertOnEnterExit]: null,
    [ACCOUNT_DEFAULT_CODE.geofenceMaxDwellDays]: null,
    [ACCOUNT_DEFAULT_CODE.geofenceMaxAssets]: null,
    [ACCOUNT_DEFAULT_CODE.geofenceMinAssets]: null,
  })
    .then(() => {
      alertOnEnterExit.value = false;
      maxDwellDays.value = null;
      maxAssets.value = null;
      minAssets.value = null;
      toast.success(COMMON_UPDATE_SUCCESS);
    })
    .catch(() => toast.error('Could not update geofence defaults'));

setTitle('Geofence Settings');
</script>

<template>
  <section class="max-w-[860px]">
    <HeadingSection
      class="mb-[35px]"
      desc="Starting values for new geofences. Anyone can still change any value on an individual geofence."
      heading="Geofences"
    />
    <SettingsSettingRow
      title="Alert on enter and exit"
      desc="When on, new geofences alert when an asset enters or leaves"
    >
      <UIToggle
        v-model="alertOnEnterExit"
        aria-label="Alert on enter and exit"
      />
    </SettingsSettingRow>
    <SettingsSettingRow
      title="Max time in geofence"
      desc="Days an asset may dwell before it breaches. Leave empty for no limit."
    >
      <div class="w-[120px] sm:justify-self-end">
        <InputNumber
          v-model="maxDwellDays"
          input-id="default_geofence_max_dwell_days"
          placeholder="None"
          :min="0"
          :max="PARAMETER_MAX"
          show-buttons
          fluid
        />
      </div>
    </SettingsSettingRow>
    <SettingsSettingRow
      title="Max number of assets"
      desc="Most that may be inside at once. Leave empty for no limit."
    >
      <div class="w-[120px] sm:justify-self-end">
        <InputNumber
          v-model="maxAssets"
          input-id="default_geofence_max_assets"
          placeholder="None"
          :min="0"
          :max="PARAMETER_MAX"
          show-buttons
          fluid
        />
      </div>
    </SettingsSettingRow>
    <SettingsSettingRow
      title="Min number of assets"
      desc="Fewest that must be inside at once. Leave empty for no limit."
    >
      <div class="w-[120px] sm:justify-self-end">
        <InputNumber
          v-model="minAssets"
          input-id="default_geofence_min_assets"
          placeholder="None"
          :min="0"
          :max="PARAMETER_MAX"
          show-buttons
          fluid
        />
      </div>
    </SettingsSettingRow>
    <div
      class="mt-[24px] flex items-center gap-x-[16px] border-t border-t-gpx_gray-800 pt-[24px]"
    >
      <Button
        class="w-48"
        :loading="isSaving"
        loadingText="Saving..."
        :disabled="isSaving"
        @click="onSave"
      >
        Save defaults
      </Button>
      <button
        class="text-sm text-gpx_gray-100 hover:text-gpx_black-200"
        :disabled="isSaving"
        @click="onReset"
      >
        Reset to built-in defaults
      </button>
    </div>
    <p
      class="mt-[16px] max-w-[58ch] border-l-2 border-l-gpx_gray-700 pl-[11px] text-xs text-gpx_gray-200"
    >
      Changing a default affects geofences created from now on. Existing
      geofences are never modified. The allowable parameters appear on the
      geofence form for business accounts only.
    </p>
  </section>
</template>
